import type { ApolloError } from "@apollo/client";
import type { SxProps, Theme } from "@mui/material";
import { Alert, Box } from "@mui/material";
import { forwardRef, type ReactNode, useEffect, useImperativeHandle } from "react";

import { GetActivitiesQuery } from "@/graphql/graphql";
import { graphqlClientErrorMessage } from "@/utils/format";

import ActivitiesTable from "./ActivitiesTable";
import type { ActivitiesQueryState } from "./ActivitiesTable.activitiesQueryState";
import ActivitiesTableFilters from "./ActivitiesTableFilters";
import { useActivitiesListQuery } from "./useActivitiesListQuery";

type PlanActivityRow = GetActivitiesQuery["activities"]["activities"][number];

export type ActivitiesTableWithDataRef = {
  /** Clears search inputs and list query state (e.g. after a successful call-plan save). */
  resetList: () => void;
};

type ActivitiesListEndSlotContext = {
  queryState: ActivitiesQueryState;
  typeKeysSelected: Record<string, boolean>;
};

type ActivitiesTableWithDataProps = {
  accountId?: number;
  /** When true, skips `GetActivities` and `GetActivityTypes`. */
  skip: boolean;
  /** Admin activities library: hides plan columns. */
  libraryMode?: boolean;
  /** Renders a query error `Alert` above the filters (admin activities page). */
  showQueryErrorAlert?: boolean;
  filterWrapSx?: SxProps<Theme>;
  /** Wraps the table and optional `endSlot` (e.g. `modalMainColumnSx` or the library table area). */
  mainColumnWrapSx?: SxProps<Theme>;
  endSlot?: ReactNode | ((ctx: ActivitiesListEndSlotContext) => ReactNode);
  /**
   * Notifies parent of list query errors (e.g. call-plan modal snackbar combines with save errors).
   */
  onQueryErrorsChange?: (errors: {
    activitiesError?: ApolloError;
    activityTypesError?: ApolloError;
  }) => void;
  activityIdsSelected?: Record<number, boolean>;
  atCapacity?: boolean;
  onAddActivity?: (activity: PlanActivityRow) => void;
  onRemoveActivity?: (activityId: number) => void;
};

type ActivitiesTableWithDataLayoutProps = {
  showQueryErrorAlert: boolean;
  listError: ApolloError | undefined;
  filterWrapSx: SxProps<Theme> | undefined;
  filterBlock: ReactNode;
  needsMainColumn: boolean;
  mainColumnWrapSx: SxProps<Theme> | undefined;
  tableNode: ReactNode;
  endContent: ReactNode | undefined;
};

const ActivitiesTableWithDataLayout = ({
  showQueryErrorAlert,
  listError,
  filterWrapSx,
  filterBlock,
  needsMainColumn,
  mainColumnWrapSx,
  tableNode,
  endContent,
}: ActivitiesTableWithDataLayoutProps) => {
  let errorAlert: ReactNode = null;
  if (showQueryErrorAlert && listError) {
    errorAlert = (
      <Alert severity="error" sx={{ flexShrink: 0, mb: 2 }}>
        {graphqlClientErrorMessage(listError, "Could not load activities or filter options.")}
      </Alert>
    );
  }

  let filtersSection: ReactNode = null;
  if (filterWrapSx) {
    filtersSection = <Box sx={filterWrapSx}>{filterBlock}</Box>;
  } else {
    filtersSection = filterBlock;
  }

  let tableSection: ReactNode = null;
  if (needsMainColumn) {
    tableSection = (
      <Box sx={mainColumnWrapSx}>
        {tableNode}
        {endContent}
      </Box>
    );
  } else {
    tableSection = tableNode;
  }

  return (
    <>
      {errorAlert}
      {filtersSection}
      {tableSection}
    </>
  );
};

export const ActivitiesTableWithData = forwardRef<
  ActivitiesTableWithDataRef,
  ActivitiesTableWithDataProps
>(
  (
    {
      accountId,
      skip,
      showQueryErrorAlert = false,
      filterWrapSx,
      mainColumnWrapSx,
      endSlot,
      onQueryErrorsChange,
      libraryMode = false,
      activityIdsSelected = {},
      atCapacity = false,
      onAddActivity,
      onRemoveActivity,
    },
    ref
  ) => {
    const {
      titleSearchInput,
      setTitleSearchInput,
      contentSearchInput,
      setContentSearchInput,
      queryState,
      dispatchQuery,
      filtersReady,
      sortedActivityTypes,
      hasActiveFilters,
      handleClearAllFilters,
      typeKeysSelected,
      activitiesLoading,
      activities,
      totalActivities,
      handlePageChange,
      handleRowsPerPageChange,
      activitiesError,
      activityTypesError,
      resetList,
    } = useActivitiesListQuery({ accountId, skip });

    const endSlotContext: ActivitiesListEndSlotContext = { queryState, typeKeysSelected };

    const endContent = typeof endSlot === "function" ? endSlot(endSlotContext) : endSlot;

    useImperativeHandle(
      ref,
      () => ({
        resetList,
      }),
      [resetList]
    );

    useEffect(() => {
      onQueryErrorsChange?.({ activitiesError, activityTypesError });
    }, [activitiesError, activityTypesError, onQueryErrorsChange]);

    const listError = activitiesError ?? activityTypesError;

    const filterBlock = (
      <ActivitiesTableFilters
        titleSearchInput={titleSearchInput}
        onTitleSearchInputChange={setTitleSearchInput}
        contentSearchInput={contentSearchInput}
        onContentSearchInputChange={setContentSearchInput}
        queryState={queryState}
        dispatchQuery={dispatchQuery}
        filtersReady={filtersReady}
        sortedActivityTypes={sortedActivityTypes}
        hasActiveFilters={hasActiveFilters}
        onClearAllFilters={handleClearAllFilters}
      />
    );

    const tableNode = (
      <ActivitiesTable
        libraryMode={libraryMode}
        activitiesLoading={activitiesLoading}
        activities={activities}
        totalActivities={totalActivities}
        queryState={queryState}
        dispatchQuery={dispatchQuery}
        typeKeysSelected={typeKeysSelected}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        activityIdsSelected={activityIdsSelected}
        atCapacity={atCapacity}
        onAddActivity={onAddActivity}
        onRemoveActivity={onRemoveActivity}
      />
    );

    const needsMainColumn = mainColumnWrapSx != null || endContent != null;

    return (
      <ActivitiesTableWithDataLayout
        showQueryErrorAlert={showQueryErrorAlert}
        listError={listError}
        filterWrapSx={filterWrapSx}
        filterBlock={filterBlock}
        needsMainColumn={needsMainColumn}
        mainColumnWrapSx={mainColumnWrapSx}
        tableNode={tableNode}
        endContent={endContent}
      />
    );
  }
);

ActivitiesTableWithData.displayName = "ActivitiesTableWithData";
